import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Sun, MapPin, Camera, Compass, LogOut, Menu, X, Plus, Trash2, Navigation
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const createMarkerIcon = (isSelected = false) => {
  return L.divIcon({
    className: 'custom-marker-container',
    html: `<div style="width: ${isSelected ? 24 : 16}px; height: ${isSelected ? 24 : 16}px; background: ${isSelected ? '#F59E0B' : '#6366F1'}; border: 3px solid white; border-radius: 50%; box-shadow: 0 0 10px rgba(${isSelected ? '245, 158, 11' : '99, 102, 241'}, 0.5);"></div>`,
    iconSize: [isSelected ? 24 : 16, isSelected ? 24 : 16],
    iconAnchor: [isSelected ? 12 : 8, isSelected ? 12 : 8]
  });
};

const MapClickHandler = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      onLocationSelect({ lat: e.latlng.lat, lng: e.latlng.lng });
    }
  });
  return null;
};

const SpotsPage = () => {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [spots, setSpots] = useState([]);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newSpot, setNewSpot] = useState({
    name: '',
    latitude: 52.2297,
    longitude: 21.0122,
    description: '',
    tags: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSpots();
  }, []);

  const fetchSpots = async () => {
    try {
      const response = await axios.get(`${API_URL}/spots`);
      setSpots(response.data);
    } catch (error) {
      console.error('Failed to fetch spots:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSpot = async () => {
    if (!newSpot.name.trim()) {
      toast.error(t('spots.nameRequired'));
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/spots`, newSpot);
      setSpots([...spots, response.data]);
      setIsDialogOpen(false);
      setNewSpot({ name: '', latitude: 52.2297, longitude: 21.0122, description: '', tags: [] });
      toast.success(t('spots.createdSuccess'));
    } catch (error) {
      toast.error(t('spots.createError'));
    }
  };

  const handleDeleteSpot = async (spotId) => {
    try {
      await axios.delete(`${API_URL}/spots/${spotId}`);
      setSpots(spots.filter(s => s.id !== spotId));
      if (selectedSpot?.id === spotId) setSelectedSpot(null);
      toast.success(t('spots.deletedSuccess'));
    } catch (error) {
      toast.error(t('spots.deleteError'));
    }
  };

  const handleMapClick = (location) => {
    setNewSpot({ ...newSpot, latitude: location.lat, longitude: location.lng });
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setNewSpot({
            ...newSpot,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          toast.success(t('spots.locationUpdated'));
        },
        () => {
          toast.error(t('spots.locationError'));
        }
      );
    }
  };

  return (
    <div className="h-screen bg-slate-950 flex overflow-hidden">
      {/* Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.aside
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            className="w-[280px] bg-slate-900/50 border-r border-white/10 flex flex-col z-20"
          >
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg gradient-golden-hour flex items-center justify-center">
                  <Sun className="w-6 h-6 text-black" />
                </div>
                <span className="font-heading font-semibold text-xl text-white">LuminaSpot</span>
              </div>
            </div>

            <nav className="flex-1 p-4 space-y-2">
              <Link to="/dashboard">
                <Button variant="ghost" className="w-full justify-start gap-3 text-slate-300 hover:text-white hover:bg-white/5" data-testid="nav-dashboard">
                  <Compass className="w-5 h-5" />
                  {t('nav.planner')}
                </Button>
              </Link>
              <Link to="/spots">
                <Button variant="ghost" className="w-full justify-start gap-3 text-amber-500 bg-amber-500/10" data-testid="nav-spots">
                  <MapPin className="w-5 h-5" />
                  {t('nav.spots')}
                </Button>
              </Link>
              <Link to="/equipment">
                <Button variant="ghost" className="w-full justify-start gap-3 text-slate-300 hover:text-white hover:bg-white/5" data-testid="nav-equipment">
                  <Camera className="w-5 h-5" />
                  {t('nav.equipment')}
                </Button>
              </Link>
            </nav>

            <div className="p-4 border-t border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center">
                    <span className="text-amber-500 font-medium">{user?.name?.charAt(0).toUpperCase()}</span>
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{user?.name}</p>
                    <p className="text-slate-500 text-xs">{user?.email}</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={handleLogout} className="text-slate-400 hover:text-white" data-testid="logout-btn">
                  <LogOut className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 bg-slate-900/50 border-b border-white/10 flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-slate-400 hover:text-white"
              data-testid="sidebar-toggle"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            <h1 className="font-heading font-semibold text-xl text-white">{t('spots.title')}</h1>
          </div>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 text-black hover:bg-amber-400 font-bold rounded-none uppercase tracking-wider text-sm btn-tactile" data-testid="add-spot-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('spots.addSpot')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-white/10 max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-heading text-white">{t('spots.createSpot')}</DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('spots.spotName')}</Label>
                  <Input
                    value={newSpot.name}
                    onChange={(e) => setNewSpot({ ...newSpot, name: e.target.value })}
                    placeholder={t('spots.namePlaceholder')}
                    className="bg-slate-800 border-white/10"
                    data-testid="spot-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">{t('spots.description')}</Label>
                  <Textarea
                    value={newSpot.description}
                    onChange={(e) => setNewSpot({ ...newSpot, description: e.target.value })}
                    placeholder={t('spots.descriptionPlaceholder')}
                    className="bg-slate-800 border-white/10"
                    data-testid="spot-description-input"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300">{t('spots.location')}</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleUseCurrentLocation}
                      className="text-amber-500 hover:text-amber-400"
                      data-testid="use-current-location-btn"
                    >
                      <Navigation className="w-4 h-4 mr-1" />
                      {t('spots.useMyLocation')}
                    </Button>
                  </div>
                  <div className="h-[200px] rounded-lg overflow-hidden border border-white/10">
                    <MapContainer
                      center={[newSpot.latitude, newSpot.longitude]}
                      zoom={10}
                      className="h-full w-full"
                      zoomControl={false}
                    >
                      <TileLayer
                        url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                        attribution='&copy; OpenStreetMap'
                      />
                      <Marker position={[newSpot.latitude, newSpot.longitude]} icon={createMarkerIcon(true)} />
                      <MapClickHandler onLocationSelect={handleMapClick} />
                    </MapContainer>
                  </div>
                  <p className="text-xs text-slate-500">{t('spots.clickMapHint')}</p>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <Label className="text-xs text-slate-500">{t('spots.latitude')}</Label>
                      <Input
                        type="number"
                        step="0.0001"
                        value={newSpot.latitude}
                        onChange={(e) => setNewSpot({ ...newSpot, latitude: parseFloat(e.target.value) })}
                        className="bg-slate-800 border-white/10 font-mono text-sm"
                        data-testid="spot-lat-input"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-slate-500">{t('spots.longitude')}</Label>
                      <Input
                        type="number"
                        step="0.0001"
                        value={newSpot.longitude}
                        onChange={(e) => setNewSpot({ ...newSpot, longitude: parseFloat(e.target.value) })}
                        className="bg-slate-800 border-white/10 font-mono text-sm"
                        data-testid="spot-lng-input"
                      />
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleCreateSpot}
                  className="w-full bg-amber-500 text-black hover:bg-amber-400 font-bold rounded-none uppercase tracking-wider btn-tactile"
                  data-testid="save-spot-btn"
                >
                  {t('spots.saveSpot')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </header>

        <div className="flex-1 flex overflow-hidden">
          {/* Map */}
          <div className="flex-1 relative">
            <MapContainer
              center={[52.2297, 21.0122]}
              zoom={6}
              className="h-full w-full"
              zoomControl={false}
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; OpenStreetMap'
              />
              {spots.map((spot) => (
                <Marker
                  key={spot.id}
                  position={[spot.latitude, spot.longitude]}
                  icon={createMarkerIcon(selectedSpot?.id === spot.id)}
                  eventHandlers={{
                    click: () => setSelectedSpot(spot)
                  }}
                />
              ))}
            </MapContainer>
          </div>

          {/* Spots List */}
          <div className="w-[380px] bg-slate-900/30 border-l border-white/10 overflow-y-auto p-6">
            <div className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : spots.length === 0 ? (
                <div className="text-center py-12">
                  <MapPin className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">{t('spots.noSpots')}</p>
                  <p className="text-slate-500 text-sm">{t('spots.noSpotsHint')}</p>
                </div>
              ) : (
                spots.map((spot) => (
                  <Card
                    key={spot.id}
                    className={`bg-slate-900/50 border-white/10 cursor-pointer transition-all ${
                      selectedSpot?.id === spot.id ? 'border-amber-500/50 ring-1 ring-amber-500/30' : 'hover:border-white/20'
                    }`}
                    onClick={() => setSelectedSpot(spot)}
                    data-testid={`spot-card-${spot.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-white mb-1">{spot.name}</h3>
                          <p className="text-xs text-slate-500 font-mono">
                            {spot.latitude.toFixed(4)}°, {spot.longitude.toFixed(4)}°
                          </p>
                          {spot.description && (
                            <p className="text-sm text-slate-400 mt-2 line-clamp-2">{spot.description}</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSpot(spot.id);
                          }}
                          className="text-slate-500 hover:text-red-500"
                          data-testid={`delete-spot-${spot.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpotsPage;
